import React, { FC, useContext, useEffect, useState, useMemo, useCallback, PropsWithChildren } from 'react'
import SubsectionTitle from '../commonComponents/subsectionTitle/subsectionTitle'
import { Button, Icon, classNames } from '@hermes/design-system'
import {
  container,
  showAllButton,
  mainTitle,
  mainTitleMedium,
  minimalShowMore,
  gapForMinimalContainer,
  gapForFullContainer,
  wrapperContainer,
  minimalShowMoreContainer,
  chevronRightIcon,
  providerTreats,
  providerTreatsContainer
} from './areasOfExpertise.css'
import { ProfileContext } from '../../../providers/AppData/Profile'
import { AppDataContext } from '../../../providers/AppData'
import { ProfileKeyword } from '../../../types/keywords'
import AreasOfExpertiseModal from './AreasOfExpertiseModal'
import useTranslateMessage from '../../../hooks/useTranslateMessage'
import { Chip } from './Chip'
import { SearchReviewsContext } from '../../../providers/SearchReviews'
import { AreasSortingTabs } from '../../../constants/profile'
import SortingRatingBar from './SortingRatingBar'
import AreaOfExpertiseTitle from './AreaOfExpertiseTitle'
import ListItem from './ListItem'
import { getKeywords } from './utils/getKeywords'

const SLICE_NUMBER_FOR_FULL_VIEW = 5
const SLICE_NUMBER_FOR_MINIMAL_VIEW = 3

export type TransformedKeywordType = {
  name: string
  countInReviews: number
  countInPeerRecommendations: number
  letter?: string
  id?: string
}

type AreasOfExpertiseProps = {
  isFullView?: boolean
}

interface WrapperProps extends PropsWithChildren {
  isMinimal?: boolean
}

const Wrapper: FC<WrapperProps> = ({ isMinimal, children }) => {
  if (isMinimal) {
    return <div className={wrapperContainer}>{children}</div>
  }

  return children
}

const AreasOfExpertise: FC<AreasOfExpertiseProps> = ({ isFullView = true }) => {
  const [transformedKeywords, setTransformedKeywords] = useState<TransformedKeywordType[]>([])
  const [sortingOptionToHide, setSortingOptionToHide] = useState<AreasSortingTabs | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { specialist, practice } = useContext(ProfileContext)
  const { language } = useContext(AppDataContext)
  const translate = useTranslateMessage()

  const keywords = useMemo(() => getKeywords(specialist, practice), [specialist, practice])

  const {
    state: {
      filters: { keyword }
    },
    changeKeyword
  } = useContext(SearchReviewsContext)

  const mappedKeywords = useMemo(() => {
    return keywords.map((keyword: ProfileKeyword) => ({
      name: keyword.name[language],
      countInReviews: keyword?.countInReviews || 0,
      countInPeerRecommendations: keyword?.countInPeerRecommendations || 0
    }))
  }, [keywords, language])

  const sortedKeywords = useMemo(() => {
    return mappedKeywords
      .filter((item: TransformedKeywordType) => !!item.countInReviews || !!item.countInPeerRecommendations)
      .sort((a: TransformedKeywordType, b: TransformedKeywordType) => a.name.localeCompare(b.name))
  }, [mappedKeywords])

  useEffect(() => {
    setTransformedKeywords(sortedKeywords)
  }, [sortedKeywords, language])

  const handleAlphabetSort = () => {
    setTransformedKeywords(sortedKeywords)
    setSortingOptionToHide(null)
  }

  const handleReviewsSort = () => {
    if (isFullView) {
      setTransformedKeywords(
        mappedKeywords
          .filter((item: TransformedKeywordType) => !!item.countInReviews)
          .sort((a: TransformedKeywordType, b: TransformedKeywordType) => a.name.localeCompare(b.name))
      )
    } else {
      handleShowAllClick()
    }
    setSortingOptionToHide(AreasSortingTabs.ENDORSEMENTS)
  }

  const handleEndorsementsSort = () => {
    if (isFullView) {
      setTransformedKeywords(
        mappedKeywords
          .filter((item: TransformedKeywordType) => !!item.countInPeerRecommendations)
          .sort((a: TransformedKeywordType, b: TransformedKeywordType) => a.name.localeCompare(b.name))
      )
    } else {
      handleShowAllClick()
    }
    setSortingOptionToHide(AreasSortingTabs.REVIEWS)
  }

  const currentRatingNumber = useCallback(
    (number: number) => {
      if (sortingOptionToHide === AreasSortingTabs.REVIEWS) {
        return 0
      }
      return number
    },
    [sortingOptionToHide]
  )

  const currentEndorsementsNumber = useCallback(
    (number: number) => {
      if (sortingOptionToHide === AreasSortingTabs.ENDORSEMENTS) {
        return 0
      }
      return number
    },
    [sortingOptionToHide]
  )

  const numberOfOtherKeywords = useMemo(() => keywords.length, [keywords])

  const summaryCountReviews = useMemo(
    () => keywords.reduce((total: number, item: ProfileKeyword) => total + (item.countInReviews || 0), 0),
    [keywords]
  )
  const summaryCountEndorsements = useMemo(
    () => keywords.reduce((total: number, item: ProfileKeyword) => total + (item.countInPeerRecommendations || 0), 0),
    [keywords]
  )

  const handleShowAllClick = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleKeywordClick = (keyword: TransformedKeywordType) => {
    changeKeyword(keyword.name)
  }

  const sliceNumber = useMemo(
    () => (isFullView ? SLICE_NUMBER_FOR_FULL_VIEW : SLICE_NUMBER_FOR_MINIMAL_VIEW),
    [isFullView]
  )

  const slicedKeywords = useMemo(() => {
    if (isFullView) {
      return transformedKeywords
    }

    return transformedKeywords.slice(0, sliceNumber)
  }, [transformedKeywords, sliceNumber, isFullView])

  return (
    <Wrapper isMinimal={!isFullView}>
      <div
        className={classNames(container, {
          [gapForMinimalContainer]: !isFullView,
          [gapForFullContainer]: isFullView
        })}
      >
        <SubsectionTitle>
          {isFullView ? <SubsectionTitle.Image name="shield_star" /> : null}
          <SubsectionTitle.Text
            className={classNames({
              [mainTitleMedium]: !isFullView,
              [mainTitle]: isFullView
            })}
            title={translate('profile.areas-of-expertise.title')}
          />
        </SubsectionTitle>
        {keyword ? (
          <div className={providerTreatsContainer}>
            <span className={providerTreats}>{translate('profile.areas-of-expertise.provider-treats')}</span>
            <Chip name={keyword} />
          </div>
        ) : null}
        {summaryCountReviews && summaryCountEndorsements ? (
          <SortingRatingBar
            handleAlphabetSort={handleAlphabetSort}
            handleReviewsSort={handleReviewsSort}
            handleEndorsementsSort={handleEndorsementsSort}
            reviewsCount={summaryCountReviews}
            endorsementsCount={summaryCountEndorsements}
            isFullView={isFullView}
            namingOptions={{
              alphabet: {
                az: translate('global.az'),
                all: translate('global.all')
              },
              reviews: translate('global.reviews'),
              endorsements: translate('global.endorsements')
            }}
          />
        ) : null}
        <AreaOfExpertiseTitle isFullView={isFullView} summaryCountReviews={summaryCountReviews} translate={translate} />
        {slicedKeywords.map((keyword, index) => (
          <ListItem
            key={keyword.name}
            keyword={keyword}
            index={index}
            sliceNumber={sliceNumber}
            currentRatingNumber={currentRatingNumber}
            currentEndorsementsNumber={currentEndorsementsNumber}
            handleKeywordClick={handleKeywordClick}
          />
        ))}
        {isFullView ? (
          <Button className={showAllButton} variant="secondary" onClick={handleShowAllClick}>
            {translate('profile.areas-of-expertise.show-all', { number: numberOfOtherKeywords })}
          </Button>
        ) : (
          <div className={minimalShowMoreContainer} onClick={handleShowAllClick}>
            <span className={minimalShowMore}>
              {translate('profile.areas-of-expertise.show-more', { number: numberOfOtherKeywords })}
            </span>
            <Icon name="chevron_right" className={chevronRightIcon} />
          </div>
        )}
        <AreasOfExpertiseModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          summaryCountReviews={summaryCountReviews}
          summaryCountEndorsements={summaryCountEndorsements}
          sortingOptionToHide={sortingOptionToHide}
        />
      </div>
    </Wrapper>
  )
}

export default AreasOfExpertise
